import { GROK_WORKER_URL } from '../config.js';
import { fetchCache } from './fetchCache.js';
import { storage } from './storage.js';
import { tokenManager } from './TokenManager.js';

const STORAGE_KEY = 'openrouter_key_configured';
const VOICE_STORAGE_KEY = 'openrouter_use_for_voice';
export const OPENROUTER_KEY_STATUS_EVENT = 'openrouter-key-status';

let memoryConfigured = null;
let memoryUseForVoice = null;
let refreshInFlight = null;

const parseStoredFlag = (value) => {
  if (value === true || value === false) return value;
  return null;
};

export const getCachedOpenRouterKeyConfigured = () => {
  if (memoryConfigured === true || memoryConfigured === false) return memoryConfigured;
  const stored = parseStoredFlag(storage.get(STORAGE_KEY));
  if (stored === true || stored === false) {
    memoryConfigured = stored;
    return stored;
  }
  return null;
};

const getCachedUseForVoice = () => {
  if (memoryUseForVoice === true || memoryUseForVoice === false) return memoryUseForVoice;
  const stored = parseStoredFlag(storage.get(VOICE_STORAGE_KEY));
  if (stored === true || stored === false) {
    memoryUseForVoice = stored;
    return stored;
  }
  return null;
};

const setCachedUseForVoice = (useForVoice) => {
  const next = Boolean(useForVoice);
  memoryUseForVoice = next;
  storage.set(VOICE_STORAGE_KEY, next);
};

export const isOpenRouterVoiceByokKnown = () => {
  const configured = getCachedOpenRouterKeyConfigured();
  if (configured === null) return false;
  if (configured === false) return true;
  return getCachedUseForVoice() !== null;
};

export const isOpenRouterVoiceByokEnabled = () => (
  getCachedOpenRouterKeyConfigured() === true && getCachedUseForVoice() === true
);

export const setOpenRouterKeyConfigured = (configured) => {
  const next = Boolean(configured);
  const previous = getCachedOpenRouterKeyConfigured();
  memoryConfigured = next;
  storage.set(STORAGE_KEY, next);
  if (previous === next) return;
  document.dispatchEvent(new CustomEvent(OPENROUTER_KEY_STATUS_EVENT, {
    detail: { configured: next }
  }));
};

export const clearOpenRouterKeyStatusCache = () => {
  memoryConfigured = null;
  memoryUseForVoice = null;
  storage.remove(STORAGE_KEY);
  storage.remove(VOICE_STORAGE_KEY);
};

const getOpenRouterKeyUrl = () => `${GROK_WORKER_URL.replace(/\/+$/, '')}/v1/hangout/openrouter-key`;

export const fetchOpenRouterKeyStatus = async ({ method = 'GET', body } = {}) => {
  const authToken = await tokenManager.getValidAccessToken();
  if (!authToken) throw new Error('No valid access token');
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`
    },
    skipCache: true,
    cache: 'no-store'
  };
  if (body !== undefined) {
    options.body = typeof body === 'string' ? body : JSON.stringify(body);
  }
  const status = await fetchCache.fetch(getOpenRouterKeyUrl(), options);
  if (status && typeof status === 'object' && Object.prototype.hasOwnProperty.call(status, 'configured')) {
    const configured = Boolean(status.configured);
    setOpenRouterKeyConfigured(configured);
    setCachedUseForVoice(configured && status.use_for_voice !== false);
  }
  return status;
};

export const refreshOpenRouterKeyStatus = async () => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const status = await fetchOpenRouterKeyStatus({ method: 'GET' });
      return Boolean(status?.configured);
    } catch (error) {
      console.warn('[openRouterKeyStatus] Failed to refresh:', error);
      return getCachedOpenRouterKeyConfigured();
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};
